/**
 * Program listing — presentational view (no business logic)
 */
"use client";

import { memo, useMemo } from "react";
import styles from "../ProgramListingSection.module.scss";
import ProgramCard from "@/components/atoms/ProgramCard/ProgramCard";
import ProgramFilters from "@/components/atoms/ProgramFilters/ProgramFilters";
import ChevronDownIcon from "@/SvgIcons/ChevronDownIcon";
import ProgramFiltersIcon from "@/SvgIcons/ProgramFiltersIcon";
import type { ProgramListingSectionViewModel } from "../model/programListing.types";

function ProgramListingSectionView({
  sectionRef,
  sentinelRef,
  filterSections,
  activeFilters,
  handleFiltersChange,
  certPartnerFilters,
  toggleCertPartner,
  lockedProgramType,
  filteredProgramsCount,
  filteredCertificationsCount,
  sortBy,
  sortOpen,
  setSortOpen,
  setSortBy,
  sortOptions,
  cardProps,
  hasResults,
  clearFilters,
  hasMore,
  activeFilterCount,
  mobileFiltersOpen,
  setMobileFiltersOpen,
  certPartners,
}: ProgramListingSectionViewModel) {
  // Stable subOptions object so memo(ProgramFilters) can skip re-renders
  // when only unrelated UI state (e.g. sortOpen) changes.
  const subOptions = useMemo(
    () => ({
      programType: {
        Certifications: {
          options: [...certPartners],
          selected: certPartnerFilters,
          onToggle: toggleCertPartner,
        },
      },
    }),
    [certPartners, certPartnerFilters, toggleCertPartner],
  );

  const lockedOptions = useMemo(
    () =>
      lockedProgramType ? { programType: [lockedProgramType] } : undefined,
    [lockedProgramType],
  );

  return (
    <section ref={sectionRef} className={styles.wrapper}>
      <ProgramFilters
        sections={filterSections}
        activeFilters={activeFilters}
        onChange={handleFiltersChange}
        subOptions={subOptions}
        lockedOptions={lockedOptions}
      />

      <div className={styles.content}>
        <div className={styles.topBar}>
          <span className={styles.countText}>
            Showing{" "}
            {filteredProgramsCount > 0 && filteredCertificationsCount > 0 ? (
              <>
                <strong>{filteredProgramsCount}</strong> Programs and{" "}
                <strong>{filteredCertificationsCount}</strong> Certifications
              </>
            ) : filteredProgramsCount > 0 ? (
              <>
                <strong>{filteredProgramsCount}</strong> Programs
              </>
            ) : filteredCertificationsCount > 0 ? (
              <>
                <strong>{filteredCertificationsCount}</strong> Certifications
              </>
            ) : (
              <>0 Results</>
            )}
          </span>

          <div className={styles.sortWrap}>
            <button
              className={styles.sortBtn}
              onClick={() => setSortOpen(!sortOpen)}
              aria-haspopup="listbox"
              aria-expanded={sortOpen}
            >
              <span className={styles.sortLabel}>Sort:</span>
              <span className={styles.sortValue}>{sortBy}</span>
              <ChevronDownIcon
                size={16}
                color="#1A325D"
                className={sortOpen ? styles.chevronOpen : undefined}
              />
            </button>

            {sortOpen && (
              <ul className={styles.sortDropdown} role="listbox">
                {sortOptions.map((opt) => (
                  <li
                    key={opt}
                    role="option"
                    aria-selected={sortBy === opt}
                    className={`${styles.sortOption} ${sortBy === opt ? styles.sortOptionActive : ""}`}
                    onClick={() => {
                      setSortBy(opt);
                      setSortOpen(false);
                    }}
                  >
                    {opt}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {hasResults ? (
          <div className={styles.grid}>
            {cardProps.map((card, i) => (
              <ProgramCard key={`${card.title}-${card.href}-${i}`} {...card} />
            ))}
          </div>
        ) : (
          <div className={styles.empty}>
            <p>No programs match your filters.</p>
            <button className={styles.clearBtn} onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}

        <div
          ref={sentinelRef}
          className={`${styles.sentinel} ${!hasMore ? styles.sentinelHidden : ""}`}
          aria-hidden="true"
        >
          {hasMore && <span className={styles.spinner} />}
        </div>
      </div>

      <button
        className={styles.floatingFilterBtn}
        onClick={() => setMobileFiltersOpen(true)}
        aria-label="Open filters"
      >
        <span className={styles.floatingFilterLabel}>
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </span>
        <ProgramFiltersIcon size={22} />
      </button>

      {mobileFiltersOpen && (
        <div
          className={styles.overlay}
          onClick={() => setMobileFiltersOpen(false)}
          aria-hidden="true"
        />
      )}
      <div
        className={`${styles.mobileDrawer} ${mobileFiltersOpen ? styles.mobileDrawerOpen : ""}`}
        aria-hidden={!mobileFiltersOpen}
      >
        <div className={styles.drawerHeader}>
          <span className={styles.drawerTitle}>Filters</span>
          <button
            className={styles.drawerClose}
            onClick={() => setMobileFiltersOpen(false)}
            aria-label="Close filters"
          >
            ✕
          </button>
        </div>
        <div className={styles.drawerContent}>
          <ProgramFilters
            sections={filterSections}
            activeFilters={activeFilters}
            onChange={handleFiltersChange}
            drawerVisible
            subOptions={subOptions}
            lockedOptions={lockedOptions}
          />
        </div>
        <div className={styles.drawerFooter}>
          <button
            className={styles.applyBtn}
            onClick={() => setMobileFiltersOpen(false)}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </section>
  );
}

export default memo(ProgramListingSectionView);
